import {Actions, ButtonGroup, IconButtonActionProps, Overlay} from 'platform/components';
import {
  Box,
  Center,
  Clickable,
  ClickableProps,
  Hide,
  Icon,
  Integer,
  Show,
  ThemeIconKey,
} from 'platform/foundation';

import {
  isNotNilOrEmpty,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  TestIdProps,
  useBoolean,
} from 'shared/web';

export type PreviewAction = Pick<IconButtonActionProps, 'icon' | 'isDisabled' | 'onClick'> &
  TestIdProps;

type IconClickAction =
  | {onClick: () => void; actions?: never}
  | {actions?: PreviewAction[] | Nullish; onClick?: never};

type IconWithActionsProps = {
  iconSize: Integer;
  width: ClickableProps['width'];
  height: ClickableProps['height'];
  icon?: ThemeIconKey;
} & IconClickAction &
  RequiredTestIdProps;

export function IconWithActions(props: IconWithActionsProps) {
  const [isMouseEnter, setIsMouseEnter, setIsMouseLeave] = useBoolean(false);
  const hasInteraction = props.onClick || isNotNilOrEmpty(props.actions);

  if (!hasInteraction) {
    return (
      <Box data-testid={suffixTestId('preview', props)} width={props.width} height={props.height}>
        <CenteredIcon
          icon={props.icon}
          iconSize={props.iconSize}
          data-testid={suffixTestId('icon', props)}
        />
      </Box>
    );
  }

  return (
    <Clickable
      onClick={props.onClick}
      onMouseEnter={setIsMouseEnter}
      onMouseLeave={setIsMouseLeave}
      width={props.width}
      height={props.height}
    >
      <Box
        data-testid={suffixTestId('preview', props)}
        width="100%"
        height="100%"
        position="relative"
      >
        <CenteredIcon
          icon={props.icon}
          iconSize={props.iconSize}
          data-testid={suffixTestId('icon', props)}
        />

        <Show when={isMouseEnter}>
          <Overlay variant="neutral" density="high">
            <Hide when={props.onClick}>
              <Center width="100%" height="100%" justify="center">
                <ButtonGroup data-testid={suffixTestId('preview', props)}>
                  <Actions
                    data-testid={suffixTestId('preview', props)}
                    actions={props.actions?.map((action) => ({
                      'data-testid':
                        action['data-testid'] ??
                        suffixTestId(`preview-action-${action.icon}`, props),
                      type: 'iconButton',
                      size: 'default',
                      priority: 'secondary',
                      icon: action.icon,
                      isDisabled: action.isDisabled,
                      onClick: action.onClick,
                    }))}
                  />
                </ButtonGroup>
              </Center>
            </Hide>
          </Overlay>
        </Show>
      </Box>
    </Clickable>
  );
}

type CenteredIconProps = Pick<IconWithActionsProps, 'icon' | 'iconSize'> & TestIdProps;

function CenteredIcon(props: CenteredIconProps) {
  return (
    <Center width="100%" height="100%" justify="center">
      <Icon
        value={props.icon}
        size={props.iconSize}
        color="palettes.neutral.800.100"
        data-testid={props['data-testid']}
      />
    </Center>
  );
}
